import {
  pull,
  LocalGameStorage,
  summarizeCollection,
  rarityLabel,
  type LocalSnapshot,
  type LocalVoucherState
} from '@gachakit/core';
import type { CardData } from '@gachakit/core/types/card';
import type { PullResult } from '@gachakit/core/types/pack';
import { registerSW } from 'virtual:pwa-register';
import '../../../src/components/CardFace';
import '../../../src/components/Collection';
import '../../../src/components/DailyProgress';
import '../../../src/components/PullResult';
import './app.css';
import { FriendPacksAdapter } from '../adapter';
import { friendPacks } from '../cards';
import './PackCarousel';
import type { PackChoice } from './PackCarousel';

type Screen = 'pull' | 'collection' | 'detail' | 'settings';
type BeforeInstallPromptChoice = 'accepted' | 'dismissed';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: BeforeInstallPromptChoice; platform: string }>;
  prompt: () => Promise<void>;
}

const PACK_SIZE = 5;
const VOUCHER_CODE = 'hello2';
const VOUCHER_PACK_REWARD = 2;
const PACK_THEMES: Record<string, Pick<PackChoice, 'label' | 'primary' | 'secondary'>> = {
  'ai-press-pack': {
    label: 'Newsroom edition',
    primary: '#73e5ff',
    secondary: '#8b5cf6'
  },
  'azeroth-raid-pack': {
    label: 'Raid-night edition',
    primary: '#ffcf70',
    secondary: '#ef5f5f'
  },
  'superstar-pack': {
    label: 'Squad legends edition',
    primary: '#ff76b8',
    secondary: '#65d8ff'
  }
};
const PACK_CHOICES: PackChoice[] = friendPacks.map((pack) => {
  const theme = PACK_THEMES[pack.id] ?? {
    label: 'Mate-pack edition',
    primary: '#80dfff',
    secondary: '#f5c76b'
  };
  return {
    id: pack.id,
    name: pack.name,
    description: pack.description,
    artUrl: pack.cards[0]?.imageUrl ?? '',
    ...theme
  };
});

registerSW({ immediate: true });

const DAILY_LIMIT = Number(import.meta.env.VITE_DAILY_PULL_LIMIT ?? 10);
const PACK_LIMIT = Math.max(1, Math.floor(DAILY_LIMIT / PACK_SIZE));
const adapter = new FriendPacksAdapter();
const storage = new LocalGameStorage(adapter.id);

void adapter.prefetch(24);

let snapshot = storage.load();
let selectedCard: CardData | null = snapshot.lastResult?.card ?? snapshot.collection[0]?.card ?? null;
let revealPack: PullResult[] | null = null;
let selectedPackId = PACK_CHOICES[0]?.id ?? '';
let activeScreen: Screen = 'pull';
let loading = false;
let resetPending = false;
let resetTimer: number | null = null;
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let statusMessage = 'A sealed mate-pack is ready for the squad binder.';
let voucherInput = '';
let voucherMessage = '';

const app = document.querySelector<HTMLDivElement>('#app');

if (!app) {
  throw new Error('App root missing');
}

const appRoot = app;

window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  deferredInstallPrompt = event as BeforeInstallPromptEvent;
  statusMessage = 'Mates Gacha is ready to install as a standalone pack opener.';
  render();
});

window.addEventListener('appinstalled', () => {
  deferredInstallPrompt = null;
  statusMessage = 'Installed. The squad binder can now launch from your home screen.';
  render();
});

function render(): void {
  const summary = summarizeCollection(snapshot.collection);
  const voucherState = getVoucherState(snapshot);
  const remainingCards = Math.max(0, DAILY_LIMIT - snapshot.session.pullsToday);
  const usedPacks = Math.floor(snapshot.session.pullsToday / PACK_SIZE);
  const dailyPacksRemaining = Math.floor(remainingCards / PACK_SIZE);
  const remainingPacks = dailyPacksRemaining + voucherState.bonusPacks;
  const lastCard = snapshot.lastResult?.card ?? null;
  const selectedEntry = selectedCard
    ? snapshot.collection.find((entry) => entry.card.id === selectedCard?.id) ?? null
    : null;
  const premiumOwned =
    summary.byRarity.rare + summary.byRarity.epic + summary.byRarity.legendary;
  const installLabel = deferredInstallPrompt ? 'Install App' : 'Install Unavailable';
  const resetLabel = resetPending ? 'Confirm Reset' : 'Reset Progress';
  const packNoun = remainingPacks === 1 ? 'pack' : 'packs';
  const bonusPackNoun = voucherState.bonusPacks === 1 ? 'bonus pack' : 'bonus packs';
  const voucherStatus =
    voucherMessage || `${voucherState.bonusPacks} ${bonusPackNoun} available.`;

  appRoot.innerHTML = `
    <div class="shell">
      <header class="app-hero">
        <div class="brand-mark" aria-hidden="true">
          <span></span>
        </div>
        <div class="hero-copy">
          <span class="eyebrow">Friend-pack binder</span>
          <h1 class="title">Mates Gacha</h1>
          <p class="subtitle">Pull your mates as shiny spell cards, from AI scoops to raid-night glory.</p>
        </div>
      </header>

      <section class="screen" data-screen="pull" data-active="${activeScreen === 'pull'}">
        <article class="vault">
          <div class="vault-head">
            <div>
              <span class="eyebrow">Daily vault · ${remainingPacks > 0 ? `${remainingPacks} ${packNoun} left` : 'locked'}</span>
              <h2>Choose your pack</h2>
            </div>
            <span class="vault-ring" data-empty="${remainingPacks <= 0}">
              <strong>${remainingPacks}</strong>
              <small>${packNoun}</small>
            </span>
          </div>
          <p class="vault-status">${escapeHtml(statusMessage)}</p>
          <daily-progress></daily-progress>
          <div class="stat-grid">
            <div class="metric"><strong>${summary.uniqueCards}</strong><span>Unique</span></div>
            <div class="metric"><strong>${summary.totalCards}</strong><span>Total</span></div>
            <div class="metric"><strong>${premiumOwned}</strong><span>Rare+</span></div>
          </div>
          <pack-carousel></pack-carousel>
        </article>

        <article class="panel last-card" data-empty="${lastCard ? 'false' : 'true'}">
          <div class="section-header">
            <div>
              <h2 class="section-title">Latest pull</h2>
              <p class="section-copy">${lastCard ? 'Recently filed in your squad binder.' : 'The first card will land here after a pack reveal.'}</p>
            </div>
          </div>
          ${
            lastCard
              ? '<gacha-card id="last-card" compact></gacha-card>'
              : '<div class="empty empty-vault"><span></span><strong>No mates filed yet</strong><small>Open a sealed pack to start the binder.</small></div>'
          }
        </article>
      </section>

      <section class="screen" data-screen="collection" data-active="${activeScreen === 'collection'}">
        <article class="panel binder-panel">
          <div class="section-header">
            <div>
              <h2 class="section-title">Squad Binder</h2>
              <p class="section-copy">Image-backed mate cards, sorted by rarity and ready for inspection.</p>
            </div>
            <span class="chip">${summary.uniqueCards} unique</span>
          </div>
          <div class="rarity-strip" aria-label="Collection rarity counts">
            ${renderRarityCount('common', summary.byRarity.common)}
            ${renderRarityCount('uncommon', summary.byRarity.uncommon)}
            ${renderRarityCount('rare', summary.byRarity.rare)}
            ${renderRarityCount('epic', summary.byRarity.epic)}
            ${renderRarityCount('legendary', summary.byRarity.legendary)}
          </div>
          <gacha-collection></gacha-collection>
        </article>
      </section>

      <section class="screen" data-screen="detail" data-active="${activeScreen === 'detail'}">
        <article class="panel detail-panel">
          <div class="section-header">
            <div>
              <h2 class="section-title">${selectedCard ? escapeHtml(selectedCard.name) : 'Card detail'}</h2>
              <p class="section-copy">${
                selectedCard
                  ? `${escapeHtml(selectedCard.subtitle ?? 'Unknown mate')} - filed ${selectedEntry?.count ?? 1}x`
                  : 'Select a card from the binder.'
              }</p>
            </div>
            ${selectedCard ? `<span class="chip" data-rarity="${selectedCard.rarity}">${rarityLabel(selectedCard.rarity)}</span>` : ''}
          </div>
          ${
            selectedCard
              ? `
                <gacha-card id="detail-card" showcase data-count="${selectedEntry?.count ?? 1}"></gacha-card>
                <div class="detail-actions" data-single="${selectedCard.sourceUrl ? 'false' : 'true'}">
                  <button class="button-secondary" id="back-to-binder" type="button">Back to Binder</button>
                  ${
                    selectedCard.sourceUrl
                      ? `<a class="button-secondary" href="${escapeHtml(selectedCard.sourceUrl)}" target="_blank" rel="noreferrer">Card Source</a>`
                      : ''
                  }
                </div>
              `
              : '<div class="empty empty-vault"><span></span><strong>No card selected</strong><small>Choose a mate from the binder.</small></div>'
          }
        </article>
      </section>

      <section class="screen" data-screen="settings" data-active="${activeScreen === 'settings'}">
        <article class="panel settings-list">
          <div class="settings-card">
            <span class="settings-icon adapter-icon" aria-hidden="true"></span>
            <div>
              <strong>${escapeHtml(adapter.name)}</strong>
              <span>${escapeHtml(adapter.description)}</span>
            </div>
          </div>
          <div class="settings-card">
            <span class="settings-icon pack-icon" aria-hidden="true"></span>
            <div>
              <strong>Pack rules</strong>
              <span>${PACK_SIZE} cards per booster. Add more mates in the local card list to grow the pack pool.</span>
            </div>
          </div>
          <div class="settings-card">
            <span class="settings-icon storage-icon" aria-hidden="true"></span>
            <div>
              <strong>Local binder</strong>
              <span>Progress is stored on this device, so the joke collection survives refreshes.</span>
            </div>
          </div>
          <form class="voucher-card" id="voucher-form">
            <label for="voucher-code">
              <strong>Voucher code</strong>
              <span>${escapeHtml(voucherStatus)}</span>
            </label>
            <div class="voucher-row">
              <input id="voucher-code" type="text" autocomplete="off" spellcheck="false" placeholder="Enter code" value="${escapeAttribute(voucherInput)}" />
              <button class="button-secondary" type="submit">Redeem</button>
            </div>
          </form>
          <div class="actions">
            <button class="button-secondary" id="install-button" type="button" ${deferredInstallPrompt ? '' : 'disabled'}>${installLabel}</button>
            <button class="button-secondary danger" id="reset-button" type="button">${resetLabel}</button>
          </div>
        </article>
      </section>
    </div>

    <nav class="nav">
      ${renderNavButton('pull', 'Vault', activeScreen)}
      ${renderNavButton('collection', 'Binder', activeScreen)}
      ${renderNavButton('detail', 'Inspect', activeScreen)}
      ${renderNavButton('settings', 'Settings', activeScreen)}
    </nav>

    <pull-result></pull-result>
  `;

  const packCarousel = appRoot.querySelector('pack-carousel') as HTMLElement & {
    setState: (state: {
      choices: PackChoice[];
      selectedId: string;
      loading: boolean;
      remaining: number;
    }) => void;
  };
  packCarousel?.setState({
    choices: PACK_CHOICES,
    selectedId: selectedPackId,
    loading,
    remaining: remainingPacks
  });

  const progress = appRoot.querySelector('daily-progress') as HTMLElement & {
    setState: (state: { value: number; limit: number; label?: string; legendaryPity: number; epicPity: number }) => void;
  };
  progress?.setState({
    value: usedPacks,
    limit: PACK_LIMIT,
    label: 'packs',
    legendaryPity: snapshot.session.pullsSinceLegendary,
    epicPity: snapshot.session.pullsSinceEpic
  });

  const collection = appRoot.querySelector('gacha-collection') as HTMLElement & {
    items: typeof snapshot.collection;
  };
  if (collection) {
    collection.items = snapshot.collection;
  }

  const overlay = appRoot.querySelector('pull-result') as HTMLElement & {
    pack: PullResult[] | null;
    presentation: {
      brand?: string;
      name: string;
      label: string;
      artUrl: string;
      primary: string;
      secondary: string;
    };
  };
  const selectedPack = PACK_CHOICES.find((pack) => pack.id === selectedPackId) ?? PACK_CHOICES[0];
  if (selectedPack) {
    overlay.presentation = {
      ...selectedPack,
      brand: 'Mates Gacha'
    };
  }
  overlay.pack = revealPack;

  const lastCardElement = appRoot.querySelector('#last-card') as (HTMLElement & { card: CardData | null }) | null;
  if (lastCardElement) {
    lastCardElement.setAttribute('data-count', String(snapshot.collection.find((entry) => entry.card.id === lastCard?.id)?.count ?? 1));
    lastCardElement.card = lastCard;
  }

  const detailCardElement = appRoot.querySelector('#detail-card') as (HTMLElement & { card: CardData | null }) | null;
  if (detailCardElement) {
    detailCardElement.card = selectedCard;
  }

  appRoot.querySelectorAll<HTMLButtonElement>('[data-screen-target]').forEach((button) => {
    button.addEventListener('click', () => {
      navigateTo(button.dataset.screenTarget as Screen);
    });
  });

  packCarousel?.addEventListener('change', handlePackSelection);
  packCarousel?.addEventListener('press', handlePull, { once: true });
  collection?.addEventListener('select', handleCollectionSelect);
  collection?.addEventListener('open-pack', () => {
    navigateTo('pull');
  });
  overlay.addEventListener('close', () => {
    revealPack = null;
    render();
  });
  appRoot.querySelector('#back-to-binder')?.addEventListener('click', () => {
    navigateTo('collection');
  });
  appRoot.querySelector('#reset-button')?.addEventListener('click', handleReset);
  appRoot.querySelector('#install-button')?.addEventListener('click', () => {
    void handleInstall();
  });
  appRoot.querySelector('#voucher-form')?.addEventListener('submit', handleVoucherRedeem);
  appRoot.querySelector<HTMLInputElement>('#voucher-code')?.addEventListener('input', (event) => {
    voucherInput = (event.target as HTMLInputElement).value;
    voucherMessage = '';
  });
}

async function handlePull(): Promise<void> {
  if (loading) {
    return;
  }

  const voucherState = getVoucherState(snapshot);
  const canUseDailyPack = snapshot.session.pullsToday + PACK_SIZE <= DAILY_LIMIT;
  const canUseBonusPack = voucherState.bonusPacks > 0;
  const useBonusPack = !canUseDailyPack && canUseBonusPack;
  const selectedPack = PACK_CHOICES.find((pack) => pack.id === selectedPackId) ?? PACK_CHOICES[0];

  if (!selectedPack || (!canUseDailyPack && !canUseBonusPack)) {
    statusMessage = 'Not enough room for another full pack today. Redeem a voucher or come back after local midnight.';
    render();
    return;
  }

  loading = true;
  statusMessage = useBonusPack
    ? `Foil-wrapping a bonus ${selectedPack.name}...`
    : `Foil-wrapping your ${selectedPack.name}...`;
  render();

  try {
    const results = await pullPack(PACK_SIZE, useBonusPack, selectedPack.id);

    let nextSnapshot = storage.savePulls(results);
    if (useBonusPack) {
      const nextVoucherState = {
        ...getVoucherState(nextSnapshot),
        bonusPacks: Math.max(0, voucherState.bonusPacks - 1)
      };
      nextSnapshot = {
        ...nextSnapshot,
        vouchers: nextVoucherState
      };
      storage.save(nextSnapshot);
    }
    snapshot = nextSnapshot;

    selectedCard = results[results.length - 1]?.card ?? selectedCard;
    revealPack = results;
    activeScreen = 'pull';

    const newCount = results.filter((result) => result.isNew).length;
    const packArticle = indefiniteArticle(selectedPack.name);
    statusMessage = useBonusPack
      ? `Opened a bonus ${selectedPack.name}. ${newCount} new ${newCount === 1 ? 'card' : 'cards'} added.`
      : `Opened ${packArticle} ${selectedPack.name}. ${newCount} new ${newCount === 1 ? 'card' : 'cards'} added.`;
  } catch {
    statusMessage = 'Pack build failed. Add enough mate cards for a full booster, then try again.';
  } finally {
    loading = false;
    render();
  }
}

async function pullPack(size: number, useBonusPack = false, packId?: string): Promise<PullResult[]> {
  let session = snapshot.session;
  const pullsToday = snapshot.session.pullsToday;
  const collectionIds = snapshot.collection.map((entry) => entry.card.id);
  const packIds: string[] = [];
  const results: PullResult[] = [];
  const packAdapter = new FriendPacksAdapter(packId);

  for (let index = 0; index < size; index += 1) {
    const pulled = await pull(packAdapter, session, collectionIds, useBonusPack ? Number.MAX_SAFE_INTEGER : DAILY_LIMIT, {
      excludeIds: packIds
    });
    const result = useBonusPack
      ? {
          ...pulled,
          session: {
            ...pulled.session,
            pullsToday
          }
        }
      : pulled;

    results.push(result);
    session = result.session;
    packIds.push(result.card.id);

    if (!collectionIds.includes(result.card.id)) {
      collectionIds.push(result.card.id);
    }
  }

  return results;
}

function handlePackSelection(event: Event): void {
  const customEvent = event as CustomEvent<{ id?: string }>;
  const id = customEvent.detail?.id;
  const selected = PACK_CHOICES.find((pack) => pack.id === id);
  if (!selected) {
    return;
  }

  selectedPackId = selected.id;
  statusMessage = `${selected.name} selected. Five cards, one foil seal.`;
  const status = appRoot.querySelector<HTMLElement>('.vault-status');
  if (status) {
    status.textContent = statusMessage;
  }
}

function handleCollectionSelect(event: Event): void {
  const customEvent = event as CustomEvent<{ id?: string }>;
  const id = customEvent.detail?.id;
  if (!id) {
    return;
  }

  selectedCard = snapshot.collection.find((entry) => entry.card.id === id)?.card ?? null;
  navigateTo('detail');
}

function handleReset(): void {
  if (!resetPending) {
    resetPending = true;
    statusMessage = 'Tap confirm reset within five seconds to clear this local binder.';
    if (resetTimer !== null) {
      window.clearTimeout(resetTimer);
    }
    resetTimer = window.setTimeout(() => {
      resetPending = false;
      resetTimer = null;
      render();
    }, 5000);
    render();
    return;
  }

  if (resetTimer !== null) {
    window.clearTimeout(resetTimer);
    resetTimer = null;
  }
  resetPending = false;
  snapshot = storage.reset();
  selectedCard = null;
  revealPack = null;
  activeScreen = 'pull';
  statusMessage = 'Progress reset locally.';
  render();
}

function navigateTo(screen: Screen): void {
  activeScreen = screen;
  render();
  window.requestAnimationFrame(() => {
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  });
}

async function handleInstall(): Promise<void> {
  if (!deferredInstallPrompt) {
    statusMessage = 'Install is not available in this browser session yet.';
    render();
    return;
  }

  const prompt = deferredInstallPrompt;
  deferredInstallPrompt = null;
  await prompt.prompt();
  const choice = await prompt.userChoice;
  statusMessage =
    choice.outcome === 'accepted'
      ? 'Install accepted. Mates Gacha is moving to your home screen.'
      : 'Install dismissed. You can keep playing in the browser.';
  render();
}

function handleVoucherRedeem(event: Event): void {
  event.preventDefault();

  const code = normalizeVoucherCode(voucherInput);
  const voucherState = getVoucherState(snapshot);

  if (!code) {
    voucherMessage = 'Enter a voucher code.';
    render();
    return;
  }

  if (code !== VOUCHER_CODE) {
    voucherMessage = 'Voucher code not recognized.';
    render();
    return;
  }

  if (voucherState.redeemedCodes.includes(VOUCHER_CODE)) {
    voucherMessage = 'Voucher already redeemed on this binder.';
    render();
    return;
  }

  const nextVoucherState: LocalVoucherState = {
    redeemedCodes: [...voucherState.redeemedCodes, VOUCHER_CODE],
    bonusPacks: voucherState.bonusPacks + VOUCHER_PACK_REWARD
  };
  snapshot = {
    ...snapshot,
    vouchers: nextVoucherState
  };
  storage.save(snapshot);

  voucherInput = '';
  voucherMessage = `Voucher redeemed. ${VOUCHER_PACK_REWARD} bonus packs added.`;
  statusMessage = 'Voucher redeemed. Two free mate-packs are ready after today\'s vault.';
  render();
}

function getVoucherState(source: LocalSnapshot): LocalVoucherState {
  return {
    redeemedCodes: Array.isArray(source.vouchers?.redeemedCodes)
      ? source.vouchers.redeemedCodes.filter((code) => typeof code === 'string')
      : [],
    bonusPacks: Math.max(0, Math.floor(Number(source.vouchers?.bonusPacks ?? 0)))
  };
}

function normalizeVoucherCode(value: string): string {
  return value.trim().toLowerCase();
}

function indefiniteArticle(value: string): 'a' | 'an' {
  return /^[aeiou]/i.test(value.trim()) ? 'an' : 'a';
}

function renderRarityCount(rarity: CardData['rarity'], count: number): string {
  return `<span data-rarity="${rarity}"><strong>${count}</strong><small>${rarity}</small></span>`;
}

function renderNavButton(screen: Screen, label: string, current: Screen): string {
  return `
    <button class="nav-item" type="button" data-screen-target="${screen}" data-active="${current === screen}">
      <span class="nav-glyph ${screen}-glyph" aria-hidden="true"></span>
      <span>${label}</span>
    </button>
  `;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll('`', '&#96;');
}

render();
